import { GetterTree } from 'vuex';
import { RootState } from '@/store/types';
import {
  MobileState,
  PlanItem,
  OptionItem,
  TerminalItem,
  PlanPayload,
  OptionPayload,
  TerminalPayload,
} from '@/store/Mobile/state';

type Payload = PlanPayload | OptionPayload | TerminalPayload;
type Item = PlanItem | OptionItem | TerminalItem;

// tslint:disable-next-line:no-var-requires
const _ = require('lodash');

import { MobileConfig } from '@/data/mobileConfig';
import { OfficeConfig } from '@/data/officeConfig';

const officeConfig = new OfficeConfig();

/**
 * 管理者数
 */
const managerCount = (state: MobileState): number => {
  return _.sum(_.map(state.plans, 'count')) > 0 ? 1 : 0;
};

/**
 * 利用者数
 */
const userCount = (state: MobileState): number => {
  return _.sum(_.map(state.plans, 'count'));
};

/**
 * 数（プラン、端末など）
 */
const count = (state: MobileState) => (payload: Payload) => {
  const targetList = find(state)(payload);
  return _.sum(_.map(targetList, 'count'));
};

/**
 * プラン、端末などの情報
 */
const find = (state: MobileState) => (payload: Payload) => {
  return state[payload.stateKey].filter((item: Item) => {
    const is_equal_id = 'id' in payload ? item.id === payload.id : true;
    const is_equal_carrier =
      'carrier' in payload && 'carrier' in item
        ? item.carrier === payload.carrier
        : true;
    const is_equal_sim_type =
      'simType' in payload && 'simType' in item
        ? item.simType === payload.simType
        : true;
    const is_equal_group_id =
      'groupId' in payload && 'groupId' in item
        ? item.groupId === payload.groupId
        : true;
    return (
      is_equal_id && is_equal_carrier && is_equal_sim_type && is_equal_group_id
    );
  });
};

/**
 * 初期費用合計
 */
const initialTotalAmount = (state: MobileState) => (
  mobileConfig: MobileConfig
) => {
  let total = 0;

  // プラン申し込み手数料 + SIMカード準備料（特典も計算する）
  total += state.plans.reduce((z, plan) => {
    const p = mobileConfig.findPlan(plan.id, plan.simType);

    if (p !== undefined) {
      z += p.initCost(plan.carrier) * plan.count;
    }
    return z;
  }, 0);

  // 端末料金
  total += state.terminals.reduce((z, terminal) => {
    const t = mobileConfig.findTerminal(terminal.id);
    if (t !== undefined) {
      z += t.charge * count(state)({ stateKey: 'terminals', id: terminal.id });
    }
    return z;
  }, 0);

  return total;
};

/**
 * 月額費用合計
 */
const totalAmountOfMonth = (state: MobileState) => (
  mobileConfig: MobileConfig
) => {
  let total = 0;

  // 管理者利用料
  total += managerCount(state) > 0 ? officeConfig.manager.monthlyCharge : 0;

  // 利用者利用料
  // 4IDまで / M2M専用プラン / オフィス管理者月額料金無料対象のプラン => 無料
  const m2mCount = state.plans.reduce((z: number, plan: any) => {
    const p = mobileConfig.findPlan(plan.id, plan.simType);
    if (p !== undefined) {
      z += p.isM2m ? plan.count : 0;
    }
    return z;
  }, 0);
  const freePlanCount = state.plans.reduce((z: number, plan: any) => {
    const p = mobileConfig.findPlan(plan.id, plan.simType);
    if (p !== undefined && !p.isM2m) {
      z += p.isOfficeUserMonthlyChargeFree ? plan.count : 0;
    }
    return z;
  }, 0);
  // 月額利用料の課金対象の数
  const targetCount =
    userCount(state) - m2mCount - freePlanCount - officeConfig.user.freeCount;
  if (targetCount > 0) {
    total += officeConfig.user.monthlyCharge * targetCount;
  }

  // ユニバーサルサービス料および電話リレーサービス料
  // ドコモ + データSMSなしの場合のみ不要
  const docomo_data_count = state.plans.reduce((z, plan) => {
    return (z +=
      plan.carrier == 'docomo' && plan.simType == 'data' ? plan.count : 0);
  }, 0);
  total +=
    mobileConfig.universalAndRelayServiceCharge *
    (userCount(state) - docomo_data_count);

  // プラン月額料金
  total += state.plans.reduce((z, plan) => {
    const p = mobileConfig.findPlan(plan.id, plan.simType);
    if (p !== undefined) {
      z += (p.charge - p.campaignSimPlanFeeAmounts[plan.carrier]) * plan.count;
    }
    return z;
  }, 0);

  // オプション料金
  total += state.options.reduce((z, option) => {
    const o = mobileConfig.findOption(option.id);
    if (o !== undefined) {
      z += o.charge * option.count;
    }
    return z;
  }, 0);

  return total;
};

const getters: GetterTree<MobileState, RootState> = {
  managerCount,
  userCount,
  count,
  find,
  initialTotalAmount,
  totalAmountOfMonth,
};

export default getters;
